import { z } from 'zod';
import {
  MASSAGER_DETAIL_EMAIL_MAX,
  MASSAGER_DETAIL_LOGIN_ID_MAX,
  MASSAGER_DETAIL_PASSWORD_MAX,
} from '@/pages/main/massager/constants';

const alertTypeValue = z
  .union([z.literal(1), z.literal(2), z.literal(3)])
  .optional()
  .nullable();
const gpsRangeValue = z
  .union([z.literal(1), z.literal(2), z.literal(3)])
  .optional()
  .nullable();

/**
 * Massager detail modal form schema (email + check-in settings).
 * Validation messages per BA: 4.2.1 はり・きゅう師情報編集_Change Acupuncturist Info_A2
 */
export const massagerDetailFormSchema = z
  .object({
    email: z
      .string()
      .optional()
      .or(z.literal(''))
      .refine(
        v => !v || v.length <= MASSAGER_DETAIL_EMAIL_MAX,
        'メールアドレスは255文字以下にしてください。'
      )
      .refine(
        v => !v || v === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
        'メールアドレスを正しいメールアドレスにしてください。'
      ),
    // ログインID: 自由入力, max 100文字. 同一 clinic 内の重複は API 保存時にバリデーションされ toast で「ログインIDは既に存在します。」を表示
    login_id: z
      .string()
      .optional()
      .or(z.literal(''))
      .refine(
        v => !v || v.length <= MASSAGER_DETAIL_LOGIN_ID_MAX,
        'ログインIDは100文字以下にしてください。'
      ),
    password: z
      .string()
      .optional()
      .or(z.literal(''))
      .refine(
        v => !v || v.length <= MASSAGER_DETAIL_PASSWORD_MAX,
        'パスワードは100文字以下にしてください。'
      ),
    alertType: alertTypeValue,
    alertDay: z.string().optional().or(z.literal('')),
    alertMonth: z.string().optional().or(z.literal('')),
    gpsRange: gpsRangeValue,
  })
  .refine(
    data => {
      if (data.alertType !== 2) return true;
      const s = data.alertDay ?? '';
      return typeof s === 'string' && s.trim() !== '';
    },
    { message: '日数を入力してください。', path: ['alertDay'] }
  )
  .refine(
    data => {
      if (data.alertType !== 2) return true;
      if (!data.alertDay || data.alertDay.trim() === '') return true;
      const s = data.alertDay.trim();
      return /^-?\d+$/.test(s);
    },
    { message: '日数は整数にしてください。', path: ['alertDay'] }
  )
  .refine(
    data => {
      if (data.alertType !== 2) return true;
      if (!data.alertDay || data.alertDay.trim() === '') return true;
      const s = data.alertDay.trim();
      if (!/^-?\d+$/.test(s)) return true;
      const n = parseInt(s, 10);
      return n >= 1 && n <= 999;
    },
    { message: '日数は1〜999までにしてください。', path: ['alertDay'] }
  )
  .refine(
    data => {
      if (data.alertType !== 3) return true;
      const s = data.alertMonth ?? '';
      return typeof s === 'string' && s.trim() !== '';
    },
    { message: '月数を入力してください。', path: ['alertMonth'] }
  )
  .refine(
    data => {
      if (data.alertType !== 3) return true;
      if (!data.alertMonth || data.alertMonth.trim() === '') return true;
      const s = data.alertMonth.trim();
      return /^-?\d+$/.test(s);
    },
    { message: '月数は整数にしてください。', path: ['alertMonth'] }
  )
  .refine(
    data => {
      if (data.alertType !== 3) return true;
      if (!data.alertMonth || data.alertMonth.trim() === '') return true;
      const s = data.alertMonth.trim();
      if (!/^-?\d+$/.test(s)) return true;
      const n = parseInt(s, 10);
      return n >= 1 && n <= 99;
    },
    { message: '月数は1〜99までにしてください。', path: ['alertMonth'] }
  );

export type MassagerDetailFormData = z.infer<typeof massagerDetailFormSchema>;
